import { LegalLayout } from './Layout.tsx';

/**
 * /privacy — placeholder copy aligned with spec §11.6.0 and the
 * email-plus VPC + 24h grace mechanic actually in code. NOT yet
 * attorney-reviewed; the children's-privacy attorney pass before
 * Phase-1 launch will tighten the language and add jurisdiction-
 * specific clauses (CCPA, GDPR, state-level).
 */
export function PrivacyPage(): JSX.Element {
  return (
    <LegalLayout title="Privacy at Maysterko" lastUpdated="May 5, 2026">
      <p>
        This page is honest about what we collect, why, and how a parent can change their mind. We
        are committed to COPPA (Children&rsquo;s Online Privacy Protection Act) compliance and
        designed Maysterko so we can collect as little personal information as possible.
      </p>

      <h2>Who we are</h2>
      <p>
        Maysterko is built by ThinkAhead Lab. Contact:{' '}
        <a href="mailto:info@thinkaheadlab.com">info@thinkaheadlab.com</a>.
      </p>

      <h2>What we collect — and what we don&rsquo;t</h2>
      <p>
        <strong>From parents:</strong> Email address (so we can send the COPPA verification email +
        a welcome email) and the choices you make in the sign-up flow (which consents you said yes
        to, what age range your child is in, optional avatar selection).
      </p>
      <p>
        <strong>From children:</strong> Just a display name (we nudge against real names — a
        nickname is encouraged), an age band (5–7, 8–10, 11–12), and the 3D designs they make. We
        never collect a child&rsquo;s real name, email, photo, voice, location, or any analytics on
        their behavior.
      </p>
      <p>
        <strong>What we never collect:</strong> Biometrics, IP-address-based analytics, social media
        data, device identifiers, or any behavioral profile. No advertising, ever.
      </p>

      <h2>The 24-hour grace window</h2>
      <p>
        When a parent signs up, the child&rsquo;s designs are stored on the device only (in
        IndexedDB) until the parent clicks the email verification link. If the link isn&rsquo;t
        clicked within 24 hours, the parent account is automatically deleted and so are any
        device-side designs tied to it. This is the &ldquo;email-plus verifiable parental
        consent&rdquo; mechanic permitted under COPPA §312.5(b)(2).
      </p>

      <h2>How long we keep things</h2>
      <p>
        Designs are kept as long as the parent account exists. When a parent deletes their account,
        all designs and account data are removed within 7 days. Compliance audit logs are retained
        for 7 years per legal requirements.
      </p>

      <h2>Sharing</h2>
      <p>
        We don&rsquo;t sell or share child or parent data with third parties. We use a small number
        of operational vendors (Render for hosting, Neon for the database, Cloudflare for content
        delivery, Resend for email) bound by data processing agreements; they only see what&rsquo;s
        strictly necessary to run the service.
      </p>

      <h2>How a parent can change their mind</h2>
      <p>
        After verification, every Maysterko email includes a one-click revoke link. You can also
        email us at <a href="mailto:info@thinkaheadlab.com">info@thinkaheadlab.com</a> to request
        deletion of your account and all associated child data. We&rsquo;ll confirm and act within 7
        days.
      </p>

      <h2>Changes</h2>
      <p>
        If we change anything material in this policy, we&rsquo;ll email every verified parent with
        a summary at least 30 days before the change takes effect.
      </p>

      <hr />
      <p>
        <em>
          This is a working draft of our privacy posture, written by the team to be parent-readable.
          We&rsquo;ll have a children&rsquo;s-privacy attorney re-review the language before
          Maysterko opens to the public. If anything here is unclear, please ask:{' '}
          <a href="mailto:info@thinkaheadlab.com">info@thinkaheadlab.com</a>.
        </em>
      </p>
    </LegalLayout>
  );
}
